import type { ArticleDto, ArticleDtoSeasonalityTag, SeasonDto } from '@/ApiClient';
import type { TagOrigin } from '@components/pills/fs-pill-tag.vue';
import { useSeasons } from '@/store/seasons';
import { useActiveSeason } from '@/store/active-season';

export type ResolvedTagSeason =
  | { status: 'resolved'; season: SeasonDto }
  | { status: 'base' }
  | { status: 'ambiguous' };

/**
 * Resolve which season's tags the tags-popper should edit/display:
 *   1. activeSeason if set
 *   2. else the "latest" of the in-scope articles' first-dated seasonalities,
 *      where "latest" = the season with the lowest `order` (most current / primary)
 *   3. else 'base' if every in-scope article has only BaseSeasonality (no dated
 *      seasons anywhere) — article-level tags target each article's
 *      BaseSeasonality
 *   4. else 'ambiguous' (some articles have dated seasons we couldn't resolve)
 */
export function resolveArticleTagSeason(
  activeSeason: SeasonDto | null | undefined,
  articles: ArticleDto[]
): ResolvedTagSeason {
  if (activeSeason) return { status: 'resolved', season: activeSeason };
  if (articles.length === 0) return { status: 'ambiguous' };

  const seasons = useSeasons();
  let best: SeasonDto | null = null;
  let anyHasDated = false;
  for (const article of articles) {
    const dated = article.seasonalities.find((s) => s.id != null);
    if (!dated) continue;
    anyHasDated = true;
    const season = seasons.byId[dated.id!];
    if (!season) continue;
    if (best === null || season.order < best.order) best = season;
  }

  if (best) return { status: 'resolved', season: best };
  if (!anyHasDated) return { status: 'base' };
  return { status: 'ambiguous' };
}

export function getArticleTagIds(article: ArticleDto): number[] {
  return [...new Set(article.seasonalities.flatMap((s) => s.tags.map((t) => t.tagId)))];
}

// null = base/ambiguous; callers fall back to seasonalities[0]
export function resolveArticleDisplaySeason(article: ArticleDto): SeasonDto | null {
  const resolved = resolveArticleTagSeason(useActiveSeason().activeSeason, [article]);
  return resolved.status === 'resolved' ? resolved.season : null;
}

export function getResolvedArticleTagIds(article: ArticleDto): number[] {
  const season = resolveArticleDisplaySeason(article);
  const seasonality = season ? article.seasonalities.find((s) => s.id === season.id) : article.seasonalities[0];
  return [...new Set((seasonality?.tags ?? []).map((t) => t.tagId))];
}

export function getInheritedTagIds(article: ArticleDto): number[] {
  return [
    ...new Set(
      article.seasonalities.flatMap((s) => s.tags.filter((t) => t.origin !== 'articleSeason').map((t) => t.tagId))
    ),
  ];
}

export function getArticleTagsWithOrigin(article: ArticleDto): { tagId: number; origin: TagOrigin }[] {
  const seen = new Set<number>();
  const result: { tagId: number; origin: TagOrigin }[] = [];

  for (const s of article.seasonalities) {
    for (const t of s.tags) {
      if (seen.has(t.tagId)) continue;
      seen.add(t.tagId);
      result.push({ tagId: t.tagId, origin: t.origin as TagOrigin });
    }
  }

  return result;
}
